import {
    HTTP_INTERCEPTORS,
    HttpBackend,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer, Provider } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    BarcodeScannerInstanceToken,
    BarcodeScannerNgrxService,
    BarcodeScannerService,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigNgrxService,
    ConfigService,
    ConfigStoreModule,
    DateManipulationCheckInstanceToken,
    DateManipulationCheckService,
    DeviceContextInterceptor,
    ENDPOINTS,
    HttpOnlineInterceptor,
    MockModeInterceptorService,
    MultiTranslateHttpLoaderService,
    PaceCoreModule,
    PersistenceStoreModule,
    ServiceWorkerReadyService,
    UserMasterInstanceToken,
    UserMasterNgrxService,
    UserMasterService,
    UserMasterStoreModule,
    ValueHelperInstanceToken,
    ValueHelperNgrxService,
    ValueHelperService,
    ValueHelperStoreModule,
} from '@evorait/pace-core';
import {
    ActionUtilitiesInstanceToken,
    ActionUtilitiesService,
    FormsWriteWithPaceOfflineService,
    PaceForms2Module,
} from '@evorait/pace-forms';
import { IndexeddbOfflineStrategyService, OfflineInterceptorService, PaceOfflineModule } from '@evorait/pace-offline';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LetDirective, PushPipe } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { APP_VERSION } from '../environments/appversion';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { EquipmentsListComponent } from './equipments-list/equipments-list.component';
import { HeaderModule } from './header/header.module';
import { InterimsLimitComponent } from './interims-limit/interims-limit.component';
import { MainModule } from './main/main.module';
import { ProtocolProvisioningModule } from './protocol-provisioning/protocol-provisioning.module';
import { ConfigInterfaceService } from './services/config-interface/config-interface.service';
import { InitializationService } from './services/initialization/initialization.service';
import { HttpOnlineFiInterceptor } from './services/interceptor/http-online-fi-interceptor';
import { NotificationService } from './services/notification/notification.service';
import { OdataConfigService } from './services/odata-config/odata-config.service';
import { OdataValueHelperService } from './services/odata-value-helper/odata-value-helper.service';
import { PaceSideMenuService } from './services/pace-side-menu/pace-side-menu.service';
import { SynchronizationService } from './services/synchronization/synchronization.service';
import { FormAdapterEffectsService } from './store/effects/form-adapter-effects.service';
import { ProtocolsEffectsService } from './store/effects/protocols-effects.service';
import { ProtocolsReducer } from './store/reducers/protocols.reducer';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PaceAuth0AuthenticationModule, PaceAuth0AuthService } from '@evorait/pace-authentication';
import { combineLatest, Observable, of, take } from 'rxjs';
import {
    AngularPlugin,
    ApplicationinsightsAngularpluginErrorService,
} from '@microsoft/applicationinsights-angularplugin-js';

export function HttpLoaderFactory(httpBackend: HttpBackend): MultiTranslateHttpLoaderService {
    return new MultiTranslateHttpLoaderService(new HttpClient(httpBackend), [
        { prefix: '../assets/i18n/core/', suffix: '.json' },
        { prefix: '../assets/i18n/app/', suffix: '.json' },
        { prefix: '../assets/i18n/forms/', suffix: '.json' },
    ]);
}

export function appInitializerFactory(
    serviceWorkerReadyService: ServiceWorkerReadyService,
    auth0Service?: PaceAuth0AuthService,
): () => Observable<unknown> {
    const dependencies: Array<Observable<unknown>> = [];
    if (environment.production) {
        dependencies.push(serviceWorkerReadyService.ready$());
    }

    const auth0Configured = !!environment.auth0?.clientId;
    if (auth0Configured && auth0Service) {
        dependencies.push(auth0Service.ready$());
    }

    // this is mock-mode without service worker nor auth0
    if (dependencies.length < 1) {
        return (): Observable<void> => of(undefined);
    } else {
        return () => combineLatest(dependencies).pipe(take(1));
    }
}

const authModule = environment.auth0 ? [PaceAuth0AuthenticationModule.forRoot()] : [];

const interceptorProviders: Provider[] = [
    // http interceptor for intercepting authentication requests
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptorService, multi: true },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineInterceptor,
        multi: true,
    },
    // http interceptor for adding header params
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineFiInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DeviceContextInterceptor,
        multi: true,
    },
];

if (environment.operateInMockMode) {
    interceptorProviders.push({
        provide: HTTP_INTERCEPTORS,
        useClass: MockModeInterceptorService,
        multi: true,
    });
}

@NgModule({
    declarations: [AppComponent, InterimsLimitComponent, EquipmentsListComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FontAwesomeModule,
        ProgressbarModule,
        PopoverModule,
        AppRoutingModule,
        FormsModule,
        ModalModule,
        LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
        PaceForms2Module.forRoot({
            notificationService: NotificationService,
            formsWriteService: FormsWriteWithPaceOfflineService,
        }),
        PaceCoreModule.forRoot({
            initializationService: InitializationService,
            odataConfigService: OdataConfigService,
            odataValueHelper: OdataValueHelperService,
            sideMenuService: PaceSideMenuService,
            synchronizationService: SynchronizationService,
            configInterfaceService: ConfigInterfaceService,
            environment: environment,
            appVersion: APP_VERSION,
        }),
        PaceOfflineModule.forRoot(
            [
                ENDPOINTS.F4mReleaseLock,
                ENDPOINTS.F4mSubmitFormSet,
                ENDPOINTS.F4mSubmitParticipantCopySet,
                ENDPOINTS.DeepInsertSet,
                ENDPOINTS.DocboxUploadSet,
            ],
            IndexeddbOfflineStrategyService,
        ),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpBackend],
            },
        }),
        TooltipModule,
        StoreModule.forRoot(
            { protocols: ProtocolsReducer },
            {
                runtimeChecks: {
                    strictStateImmutability: true,
                    strictActionImmutability: true,
                    strictStateSerializability: true,
                    strictActionSerializability: true,
                    strictActionWithinNgZone: true,
                    strictActionTypeUniqueness: true,
                },
            },
        ),
        EffectsModule.forRoot([ProtocolsEffectsService, FormAdapterEffectsService]),
        ConfigStoreModule,
        ValueHelperStoreModule,
        UserMasterStoreModule,
        PersistenceStoreModule,
        // Instrumentation must be imported after importing StoreModule (config is optional)
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
        }),
        ProtocolProvisioningModule,
        HeaderModule,
        MainModule,
        FormsModule,
        LetDirective,
        PushPipe,
        ServiceWorkerModule.register('pace-pwa-worker.js', {
            enabled: true,
            registrationStrategy: 'registerImmediately',
        }),
        ...authModule,
    ],
    providers: [
        {
            provide: ConfigInstanceToken,
            useExisting: environment.enableNgRx ? ConfigNgrxService : ConfigService,
        },
        {
            provide: ValueHelperInstanceToken,
            useExisting: environment.enableNgRx ? ValueHelperNgrxService : ValueHelperService,
        },
        {
            provide: UserMasterInstanceToken,
            useExisting: environment.enableNgRx ? UserMasterNgrxService : UserMasterService,
        },
        {
            provide: BarcodeScannerInstanceToken,
            useExisting: environment.enableNgRx ? BarcodeScannerNgrxService : BarcodeScannerService,
        },
        // This app has no ngrx block in its environment; DateManipulationCheckService always
        // implements the token here — SynchronizationService now injects it.
        { provide: DateManipulationCheckInstanceToken, useExisting: DateManipulationCheckService },
        provideHttpClient(withInterceptorsFromDi()),
        ...interceptorProviders,
        {
            provide: ActionUtilitiesInstanceToken,
            useExisting: ActionUtilitiesService,
        },
        provideAppInitializer(() => {
            const serviceWorkerReadyService = inject(ServiceWorkerReadyService);
            const auth0Service = environment.auth0?.clientId ? inject(PaceAuth0AuthService) : undefined;
            return appInitializerFactory(serviceWorkerReadyService, auth0Service)();
        }),
        { provide: AngularPlugin, useValue: new AngularPlugin() },
        { provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService },
    ],
})
export class AppModule {
    public constructor(private readonly translate: TranslateService) {
        this.translate.setDefaultLang(ConfigConstants.enLocale);
        this.translate.use(ConfigConstants.enLocale);
    }
}
